import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  VendorApplicationAdminDetail,
  VendorReviewAssignRequest,
  VendorReviewDecisionRequest,
  VendorReviewRejectRequest,
  VendorReviewResubmissionRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { vendorApplicationQueryKey } from './useVendorApplicationQuery';

export type VendorReviewActionType =
  | 'assign'
  | 'start-review'
  | 'approve'
  | 'reject'
  | 'request-resubmission';

type ActionBody =
  | VendorReviewAssignRequest
  | VendorReviewDecisionRequest
  | VendorReviewRejectRequest
  | VendorReviewResubmissionRequest
  | undefined;

const ACTION_TO_PATH: Record<
  VendorReviewActionType,
  | '/admin/v1/vendor-applications/{applicationId}/assign'
  | '/admin/v1/vendor-applications/{applicationId}/start-review'
  | '/admin/v1/vendor-applications/{applicationId}/approve'
  | '/admin/v1/vendor-applications/{applicationId}/reject'
  | '/admin/v1/vendor-applications/{applicationId}/request-resubmission'
> = {
  assign: '/admin/v1/vendor-applications/{applicationId}/assign',
  'start-review': '/admin/v1/vendor-applications/{applicationId}/start-review',
  approve: '/admin/v1/vendor-applications/{applicationId}/approve',
  reject: '/admin/v1/vendor-applications/{applicationId}/reject',
  'request-resubmission': '/admin/v1/vendor-applications/{applicationId}/request-resubmission',
};

export interface VendorReviewActionInput {
  applicationId: string;
  action: VendorReviewActionType;
  body?: ActionBody;
}

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Vendor review action failed with status ${status}`, raw);
};

export const useVendorReviewActionMutation = () => {
  const qc = useQueryClient();
  return useMutation<VendorApplicationAdminDetail, Error, VendorReviewActionInput>({
    mutationFn: async ({ applicationId, action, body }) => {
      const path = ACTION_TO_PATH[action];
      const concretePath = path.replace('{applicationId}', applicationId);
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body ?? {});
      const { data, error, response } = await apiClient.raw.POST(path, {
        body: body as never,
        params: {
          path: { applicationId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: VendorApplicationAdminDetail }).data;
    },
    onSuccess: async (_data, { applicationId }) => {
      await qc.invalidateQueries({ queryKey: vendorApplicationQueryKey(applicationId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'vendor-applications'] });
    },
  });
};
